import { useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { ChevronDown, ImageMinus, Minus, Plus } from 'lucide-react'
import {
  COLLAGE_COLOR_PRESETS,
  COLLAGE_TEMPLATES,
  GRADIENT_PRESETS,
  CollageFamily,
  getCollageTemplate,
  type CollageTemplate,
} from '@/content/collageTemplates'
import { getOutputPreset, type OutputPresetId } from '@/content/instagram'
import {
  CollageBackgroundKind,
  MAX_COLLAGE_BACKGROUND_BLUR,
  MAX_COLLAGE_GAP,
  NodeKind,
  collageCellContext,
  imageNodesInOrder,
  intrinsicRatio,
  type CollageNode,
} from '@/core/document'
import { EditAction } from '@/core/history'
import { IMAGE_FILE_ACCEPT } from '@/core/image'
import { ReportingStage, surfaceError } from '@/core/reporting'
import { useEditSession } from '@/core/store/editSession'
import { MorePanelId } from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import {
  enterCollage,
  leaveCollage,
  restoreSelectedTrayNode,
  setSelectedCollageBackground,
  setSelectedCollageBackgroundBlur,
  setSelectedCollageBackgroundImage,
  setSelectedCollageGap,
  setSelectedPrintBorder,
  switchSelectedCollageTemplate,
} from '@/tools/collage/actions'
import { CollageCellPanel } from '@/tools/collage/CollageCellPanel'
import {
  DEFAULT_PLAIN_FRAME_BORDER_MM,
  FrameStyleId,
  MAX_PLAIN_FRAME_BORDER_MM,
  MIN_PLAIN_FRAME_BORDER_MM,
} from '@/tools/frame/params'
import { ChoiceGroup, ChoiceTile } from '@/ui/ChoiceTile'
import { IconSlider } from '@/ui/IconSlider'
import {
  BackgroundIllustration,
  CollageFamilyIllustration,
  CollageTemplateIllustration,
  RatioBadge,
} from '@/ui/illustrations/PanelIllustrations'
import { MoreSection } from '@/ui/MoreSection'
import { PhotoThumbnail } from '@/ui/PhotoThumbnail'
import { Button } from '@/ui/shadcn/button'
import { Separator } from '@/ui/shadcn/separator'

const FAMILY_LABELS: Readonly<Record<CollageFamily, string>> = {
  [CollageFamily.Grid]: 'Grid',
  [CollageFamily.Prints]: 'Prints',
}

const BACKGROUND_LABELS: Readonly<Record<CollageBackgroundKind, string>> = {
  [CollageBackgroundKind.Color]: 'Color',
  [CollageBackgroundKind.Gradient]: 'Gradient',
  [CollageBackgroundKind.Image]: 'Photo',
}

function configuredDefaultGradient() {
  const gradient = GRADIENT_PRESETS[0]
  if (!gradient) throw new Error('Collage gradient presets require a default entry')
  return gradient
}

function configuredDefaultColor() {
  const color = COLLAGE_COLOR_PRESETS[0]
  if (!color) throw new Error('Collage color presets require a default entry')
  return color
}

function TemplateTile({
  template,
  active,
  ratio,
  badge,
  disabled,
}: {
  template: CollageTemplate
  active: boolean
  ratio: { w: number; h: number }
  badge?: ReactNode
  disabled?: boolean
}) {
  return (
    <div className="relative min-w-0">
      <ChoiceTile
        ariaLabel={template.label}
        illustration={<CollageTemplateIllustration template={template} ratio={ratio} />}
        selected={active}
        disabled={disabled}
        data-testid={TEST_IDS.collageTemplate(template.id)}
        onClick={() => switchSelectedCollageTemplate(template.id)}
        className="w-full px-1 [&>span:first-child]:size-[72px]"
      />
      {badge ? <span className="absolute right-1.5 top-1.5 rounded bg-background/85 p-0.5 text-muted-foreground">{badge}</span> : null}
    </div>
  )
}

function BackgroundControls({ selected, pickImage }: { selected: CollageNode; pickImage: () => void }) {
  const commitHistory = useEditSession((state) => state.commitHistory)
  switch (selected.background.kind) {
    case CollageBackgroundKind.Color: {
      const background = selected.background
      return (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Background color">
          {COLLAGE_COLOR_PRESETS.map((preset) => (
            <label
              key={preset.id}
              className="relative size-9 cursor-pointer rounded-full border shadow-sm outline-offset-2 has-[:checked]:ring-2 has-[:checked]:ring-primary"
              style={{ backgroundColor: preset.color }}
            >
              <input
                type="radio"
                name="collage-background-color"
                className="sr-only"
                aria-label={preset.label}
                checked={background.color === preset.color}
                data-testid={TEST_IDS.collageBackgroundPreset(preset.id)}
                onChange={() => setSelectedCollageBackground({ kind: CollageBackgroundKind.Color, color: preset.color })}
              />
            </label>
          ))}
          <label className="relative size-9 cursor-pointer overflow-hidden rounded-full border shadow-sm" style={{ backgroundColor: background.color }}>
            <span className="sr-only">Custom background color</span>
            <input
              type="color"
              className="absolute inset-0 size-full cursor-pointer opacity-0"
              value={background.color}
              data-testid={TEST_IDS.collageBackgroundColor}
              onChange={(event) => setSelectedCollageBackground({ kind: CollageBackgroundKind.Color, color: event.currentTarget.value }, false)}
              onBlur={() => commitHistory(EditAction.EditCollageStyle)}
            />
          </label>
        </div>
      )
    }
    case CollageBackgroundKind.Gradient: {
      const background = selected.background
      return (
        <div className="space-y-3">
          <ChoiceGroup mode="single" label="Gradient preset" className="grid-cols-3">
            {GRADIENT_PRESETS.map((gradient) => (
              <ChoiceTile
                key={gradient.id}
                ariaLabel={gradient.label}
                illustration={<span className="size-full rounded-lg" style={{ background: `linear-gradient(${gradient.angleDeg}deg, ${gradient.colors[0]}, ${gradient.colors[1]})` }} />}
                selected={background.presetId === gradient.id}
                data-testid={TEST_IDS.collageGradient(gradient.id)}
                onClick={() => setSelectedCollageBackground({
                  kind: CollageBackgroundKind.Gradient,
                  presetId: gradient.id,
                  colors: [...gradient.colors],
                })}
                className="h-11 p-1 [&>span:first-child]:size-full"
              />
            ))}
          </ChoiceGroup>
          <div className="grid grid-cols-2 gap-3">
            {background.colors.map((color, index) => (
              <label key={index} className="relative h-10 cursor-pointer overflow-hidden rounded-xl border" style={{ backgroundColor: color }}>
                <span className="sr-only">Gradient color {index + 1}</span>
                <input
                  type="color"
                  value={color}
                  aria-label={`Gradient color ${index + 1}`}
                  data-testid={TEST_IDS.collageGradientColor(index)}
                  onChange={(event) => {
                    if (selected.background.kind !== CollageBackgroundKind.Gradient) throw new Error('Gradient color update requires a gradient background')
                    const colors: [string, string] = [...selected.background.colors]
                    colors[index] = event.currentTarget.value
                    setSelectedCollageBackground({ ...selected.background, colors }, false)
                  }}
                  onBlur={() => commitHistory(EditAction.EditCollageStyle)}
                  className="absolute inset-0 size-full cursor-pointer opacity-0"
                />
              </label>
            ))}
          </div>
        </div>
      )
    }
    case CollageBackgroundKind.Image:
      return <Button type="button" variant="outline" onClick={pickImage}>Change photo</Button>
    default:
      throw new Error(`Unknown collage background kind: "${String((selected.background as { kind: unknown }).kind)}"`)
  }
}

function CollageSettingsPanel({ selected }: { selected: CollageNode }) {
  const backgroundInputRef = useRef<HTMLInputElement>(null)
  const sources = useEditSession((state) => state.sources)
  const document = useEditSession((state) => state.document)
  const split = document?.output.split ?? null
  const presetId = document?.output.presetId
  if (!document || !presetId) throw new Error('Collage panel requires a configured document and output preset')
  const selectedTemplate = getCollageTemplate(selected.templateId)
  const photosInOrder = imageNodesInOrder(selected)
  const [family, setFamily] = useState(selectedTemplate.family)
  const preset = getOutputPreset(presetId)
  const presetRatio = preset.ratio ?? intrinsicRatio(selected, sources)
  const familyTemplates = COLLAGE_TEMPLATES.filter((template) => template.family === family)
  const recommended = familyTemplates.filter((template) => (template.goodFor as readonly OutputPresetId[]).includes(presetId))
  const other = familyTemplates.filter((template) => !recommended.includes(template))
  const hasEmptyCell = selected.cells.includes(null)
  const printBorders = selected.cells.flatMap((node) => (
    node?.kind === NodeKind.Frame && node.params.styleId === FrameStyleId.Plain ? [node.params.borderMm] : []
  ))
  const printBorder = printBorders[0] ?? DEFAULT_PLAIN_FRAME_BORDER_MM
  const mixedPrintBorders = printBorders.some((border) => border !== printBorder)

  const chooseBackgroundImage = () => {
    const input = backgroundInputRef.current
    if (!input) throw new Error('Collage background file picker is not configured')
    input.click()
  }

  const updateBackgroundImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file) return
    void setSelectedCollageBackgroundImage(file).catch((error: unknown) => {
      surfaceError(error, 'Could not set the collage background image.', { stage: ReportingStage.SourceLoad })
    })
  }

  const selectBackgroundKind = (kind: CollageBackgroundKind) => {
    switch (kind) {
      case CollageBackgroundKind.Color: {
        const color = configuredDefaultColor()
        setSelectedCollageBackground({ kind, color: color.color })
        return
      }
      case CollageBackgroundKind.Gradient: {
        const gradient = configuredDefaultGradient()
        setSelectedCollageBackground({ kind, presetId: gradient.id, colors: [...gradient.colors] })
        return
      }
      case CollageBackgroundKind.Image:
        chooseBackgroundImage()
        return
      default:
        throw new Error(`Unknown collage background kind: "${String(kind)}"`)
    }
  }

  return (
    <section className="space-y-5" aria-labelledby="collage-heading">
      <h2 id="collage-heading" className="font-semibold">Collage</h2>

      <ChoiceGroup mode="single" label="Collage family" className="grid-cols-2">
        {Object.values(CollageFamily).map((candidate) => (
          <ChoiceTile
            key={candidate}
            ariaLabel={FAMILY_LABELS[candidate]}
            label={FAMILY_LABELS[candidate]}
            illustration={<CollageFamilyIllustration family={candidate} />}
            selected={family === candidate}
            data-testid={TEST_IDS.collageFamily(candidate)}
            onClick={() => setFamily(candidate)}
          />
        ))}
      </ChoiceGroup>

      <ChoiceGroup mode="single" label="Collage template" className="grid-cols-3">
        {recommended.map((template) => (
          <TemplateTile key={template.id} template={template} active={template.id === selected.templateId} ratio={presetRatio} />
        ))}
      </ChoiceGroup>

      {other.length > 0 ? (
        <details className="rounded-xl border px-3 py-2" data-testid={TEST_IDS.collageOtherRatios}>
          <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium">
            Other ratios <ChevronDown className="size-4" aria-hidden="true" />
          </summary>
          <ChoiceGroup mode="single" label="Other collage templates" className="mt-3 grid-cols-3">
            {other.map((template) => {
              const targetPresetId = template.goodFor[0]
              if (!targetPresetId) throw new Error(`Collage template "${template.id}" has no configured output preset`)
              const targetPreset = getOutputPreset(targetPresetId)
              const ratio = targetPreset.ratio ?? presetRatio
              return (
                <TemplateTile
                  key={template.id}
                  template={template}
                  active={template.id === selected.templateId}
                  ratio={ratio}
                  badge={<RatioBadge ratio={ratio} />}
                  disabled={split !== null}
                />
              )
            })}
          </ChoiceGroup>
        </details>
      ) : null}

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Background</h3>
        <input ref={backgroundInputRef} type="file" className="sr-only" accept={IMAGE_FILE_ACCEPT} aria-label="Choose collage background image" onChange={updateBackgroundImage} />
        <ChoiceGroup mode="single" label="Collage background" className="grid-cols-3">
          {Object.values(CollageBackgroundKind).map((kind) => (
            <ChoiceTile
              key={kind}
              ariaLabel={BACKGROUND_LABELS[kind]}
              label={BACKGROUND_LABELS[kind]}
              illustration={<BackgroundIllustration kind={kind} />}
              selected={selected.background.kind === kind}
              data-testid={kind === CollageBackgroundKind.Image ? TEST_IDS.collageBackgroundImage : undefined}
              onClick={() => selectBackgroundKind(kind)}
            />
          ))}
        </ChoiceGroup>
        <BackgroundControls selected={selected} pickImage={chooseBackgroundImage} />
      </div>

      <MoreSection id={MorePanelId.Collage}>
        {selectedTemplate.family === CollageFamily.Grid ? (
          <IconSlider
            min={0}
            max={MAX_COLLAGE_GAP}
            step={0.005}
            value={selected.gap}
            startIcon={<Minus className="size-4" />}
            endIcon={<Plus className="size-4" />}
            formatValue={(value) => `${Math.round(value * 100)}%`}
            data-testid={TEST_IDS.collageGap}
            aria-label="Collage gap"
            onValueChange={setSelectedCollageGap}
            onValueCommit={(value) => setSelectedCollageGap(value, true)}
          />
        ) : printBorders.length > 0 ? (
          <IconSlider
            min={MIN_PLAIN_FRAME_BORDER_MM}
            max={MAX_PLAIN_FRAME_BORDER_MM}
            step={0.5}
            value={printBorder}
            startIcon={<Minus className="size-4" />}
            endIcon={<Plus className="size-4" />}
            formatValue={(value) => mixedPrintBorders ? 'Mixed' : `${value} mm`}
            data-testid={TEST_IDS.collagePrintBorder}
            aria-label="Print border width"
            onValueChange={setSelectedPrintBorder}
            onValueCommit={(value) => setSelectedPrintBorder(value, true)}
          />
        ) : null}
        {selected.background.kind === CollageBackgroundKind.Image ? (
          <IconSlider
            min={0}
            max={MAX_COLLAGE_BACKGROUND_BLUR}
            step={1}
            value={selected.background.blur}
            startIcon={<Minus className="size-4" />}
            endIcon={<Plus className="size-4" />}
            formatValue={(value) => `${value} px`}
            data-testid={TEST_IDS.collageBackgroundBlur}
            aria-label="Background image blur"
            onValueChange={setSelectedCollageBackgroundBlur}
            onValueCommit={(value) => setSelectedCollageBackgroundBlur(value, true)}
          />
        ) : null}
      </MoreSection>

      {selected.tray.length > 0 ? (
        <div className="flex flex-wrap gap-2 rounded-xl bg-muted p-2" data-testid={TEST_IDS.collageTray}>
          {selected.tray.map((node, index) => {
            const photo = imageNodesInOrder(node)[0]
            if (!photo) throw new Error(`Collage tray node "${node.id}" does not contain a photo`)
            const photoIndex = photosInOrder.findIndex((candidate) => candidate.id === photo.id)
            if (photoIndex < 0) throw new Error(`Collage tray photo "${photo.id}" is missing from reading order`)
            const source = sources.get(photo.sourceId)
            if (!source) throw new Error(`Collage tray photo source "${photo.sourceId}" is missing`)
            return (
              <button
                key={node.id}
                type="button"
                aria-label={`Put photo ${photoIndex + 1} back`}
                disabled={!hasEmptyCell}
                data-testid={TEST_IDS.collageTrayNode(index)}
                onClick={() => restoreSelectedTrayNode(index)}
                className="relative rounded-lg outline-offset-2 disabled:opacity-45"
              >
                <PhotoThumbnail document={document} image={photo} source={source} size={48} className="rounded-lg" />
                <span className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground"><Plus className="size-3" aria-hidden="true" /></span>
              </button>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

export function CollagePanel() {
  const document = useEditSession((state) => state.document)
  const selectedNodeId = useEditSession((state) => state.selectedNodeId)
  if (!document) throw new Error('Collage panel requires a loaded document')
  if (document.root.kind !== NodeKind.Collage) {
    return (
      <section className="space-y-4" aria-labelledby="collage-entry-heading">
        <h2 id="collage-entry-heading" className="font-semibold">Collage</h2>
        <ChoiceTile
          ariaLabel="Make a collage"
          label="Collage"
          illustration={<CollageFamilyIllustration family={CollageFamily.Grid} />}
          data-testid={TEST_IDS.collageEnter}
          className="w-full"
          onClick={enterCollage}
        />
      </section>
    )
  }
  const hasActiveCell = selectedNodeId !== null && collageCellContext(document.root, selectedNodeId) !== null
  return (
    <div className="space-y-5">
      <Button type="button" variant="ghost" data-testid={TEST_IDS.collageLeave} onClick={leaveCollage}>
        <ImageMinus aria-hidden="true" />
        Back to one photo
      </Button>
      {hasActiveCell ? <><CollageCellPanel /><Separator /></> : null}
      <CollageSettingsPanel selected={document.root} />
    </div>
  )
}
